import * as React from "react";
import { AlertColor, Grid } from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import { useGetPayDatesQuery, useDeletePayDateMutation } from "../../../services/cclTokenedPayrollApi";
import AddPayDateDrawer from "./addPayDateDrawer";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

// Date-only strings are parsed as local time so the displayed day doesn't shift by timezone.
const parseDate = (value: string | null | undefined): Date | null => {
    if (!value) {
        return null;
    }

    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const parsed = dateOnlyMatch
        ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
        : new Date(value);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value: Date | null | undefined): string =>
    value instanceof Date ? value.toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" }) : "";

const normalizeSelectionIds = (selection: any): Array<string | number> => {
    if (Array.isArray(selection)) {
        return selection as Array<string | number>;
    }

    if (selection?.ids instanceof Set) {
        return Array.from(selection.ids) as Array<string | number>;
    }

    if (Array.isArray(selection?.ids)) {
        return selection.ids as Array<string | number>;
    }

    return [];
};

const PayDatesPanel: React.FC = () => {
    const { data: payDatesData, isLoading: loading } = useGetPayDatesQuery();
    const [deletePayDate, { isLoading: isDeleting }] = useDeletePayDateMutation();
    const { logEvent } = useLogAccessEvent();
    const [selectedRows, setSelectedRows] = React.useState<Array<string | number>>([]);
    const [addPayDateDrawerOpen, setAddPayDateDrawerOpen] = React.useState(false);
    const [showSnackbar, setShowSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    const isAnySelected = selectedRows.length > 0;
    const sortedRows = React.useMemo(
        () =>
            [...(payDatesData ?? [])].sort(
                (a, b) => (parseDate(b.payDays)?.getTime() ?? 0) - (parseDate(a.payDays)?.getTime() ?? 0)
            ),
        [payDatesData]
    );

    const columns: GridColDef[] = [
        { field: "id", headerName: "ID", width: 140 },
        {
            field: "payDays",
            headerName: "Pay Date",
            type: "date",
            width: 160,
            valueGetter: (params) => parseDate(params.row.payDays),
            valueFormatter: (params) => formatDate(params.value),
        },
        {
            field: "created",
            headerName: "Created",
            type: "date",
            width: 160,
            valueGetter: (params) => parseDate(params.row.created),
            valueFormatter: (params) => formatDate(params.value),
        },
        { field: "createdBy", headerName: "Created By", width: 220, flex: 1 },
    ];

    const showResult = (message: string, severity: AlertColor) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setShowSnackbar(true);
    };

    const handleToolbarButtonClick = (buttonId: string) => {
        if (buttonId === "add-pay-day-button") {
            setAddPayDateDrawerOpen(true);
            return;
        }

        if (buttonId === "delete-pay-day-button") {
            if (!isAnySelected || isDeleting) {
                return;
            }

            Promise.all(selectedRows.map((id) => deletePayDate(id).unwrap()))
                .then(() => {
                    logEvent("PayrollPayDateDeleted", null);
                    setSelectedRows([]);
                    showResult("Pay Date Deleted", "success");
                })
                .catch((error) => {
                    console.error("Error deleting pay date:", error);
                    showResult("Failed to delete Pay Date", "error");
                });
        }
    };

    return (
        <Grid container spacing={0} alignItems="stretch" sx={{ justify: "space-between", flexGrow: 1 }}>
            <AddPayDateDrawer
                open={addPayDateDrawerOpen}
                onClose={() => setAddPayDateDrawerOpen(false)}
                onSaveResult={showResult}
            />
            <CclAlertSnackbar
                open={showSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setShowSnackbar(false)}
            />
            <Grid
                item
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: 1,
                    height: "calc(100vh - 220px)",
                    minHeight: 400,
                }}
            >
                <DataGridPremium
                    columns={columns}
                    rows={sortedRows}
                    loading={loading}
                    getRowId={(row) => row.id}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={(selection) =>
                        setSelectedRows(normalizeSelectionIds(selection))
                    }
                    components={{ Toolbar: CclDataGridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showSaveState: false,
                            hideSettingsButtons: true,
                            savestate: null,
                            clearstate: null,
                            anySelected: isAnySelected,
                            miscButtons: [
                                {
                                    id: "add-pay-day-button",
                                    caption: "Add Pay Date",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                },
                                {
                                    id: "delete-pay-day-button",
                                    caption: "Delete Pay Date",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                    disable: !isAnySelected || isDeleting,
                                },
                            ],
                            btnClick: handleToolbarButtonClick,
                        },
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default PayDatesPanel;